import { useCallback, useState } from "react";
import { Field } from "formik";
import PropTypes from "prop-types";

import { formatDecimalToTime, parseTimeInput } from "@/utils/timeFormatUtils";

import styles from "./LabourTimeInput.module.scss";

// Inner component that can use hooks
const LabourTimeInputField = ({
  field,
  form,
  id,
  className,
  inputClassName,
  disabled,
  min,
  max,
  showFormatHint,
  customOnBlur,
  ...rest
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const getDisplayValue = useCallback((decimalValue) => {
    if (typeof decimalValue === "number" && !Number.isNaN(decimalValue)) {
      return formatDecimalToTime(decimalValue);
    }
    return "0h 0m";
  }, []);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setShowHint(showFormatHint);

    // Clear the field value on focus if it's 0, so the placeholder becomes visible
    const currentDecimal = parseFloat(field.value) || 0;
    if (currentDecimal === 0 && form?.setFieldValue && field?.name) {
      form.setFieldValue(field.name, "");
    }
  }, [showFormatHint, field, form]);

  const handleBlur = useCallback(
    (e) => {
      setIsEditing(false);
      setShowHint(false);

      const inputValue = e.target.value.trim();
      let decimalValue = 0;

      if (inputValue) {
        // Try to parse the input in various formats
        decimalValue = parseTimeInput(inputValue);

        // If parsing failed, try as a plain decimal number
        if (decimalValue === 0 && !Number.isNaN(parseFloat(inputValue))) {
          decimalValue = parseFloat(inputValue);
        }
      }

      // Validate the decimal value
      let validatedValue = decimalValue;
      if (min !== undefined && validatedValue < min) {
        validatedValue = min;
      }
      if (max !== undefined && validatedValue > max) {
        validatedValue = max;
      }

      // Update the Formik field value with the decimal value
      if (form?.setFieldValue && field?.name) {
        try {
          form.setFieldValue(field.name, validatedValue.toString());
        } catch (error) {
          console.error("Error setting field value:", error);
        }
      }

      // Call Formik's field onBlur
      if (field?.onBlur) {
        try {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              name: field.name,
              value: validatedValue.toString(),
            },
          };
          field.onBlur(syntheticEvent);
        } catch (error) {
          console.error("Error calling field onBlur:", error);
        }
      }

      // Call custom onBlur if provided
      if (customOnBlur && typeof customOnBlur === "function") {
        try {
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: validatedValue.toString(),
            },
          };
          customOnBlur(syntheticEvent);
        } catch (error) {
          console.error("Error calling custom onBlur:", error);
        }
      }
    },
    [field, form, min, max, customOnBlur]
  );

  const handleChange = useCallback(
    (e) => {
      // For regular onChange, just pass through to Formik
      if (field?.onChange) {
        field.onChange(e);
      }
    },
    [field]
  );

  // Determine what to display
  // When editing: show raw field value so user can type freely
  // When not editing: show formatted time
  const currentValue = parseFloat(field.value) || 0;
  const displayValue = isEditing ? field.value : getDisplayValue(currentValue);

  return (
    <div className={`${styles.container} ${className || ""}`}>
      <input
        {...rest}
        id={id}
        name={field.name}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        autoComplete="off"
        className={`${styles.input} ${inputClassName || ""}`}
        placeholder="0h 0m"
      />
      {showHint && (
        <div className={styles.formatHint}>
          Enter time in hours or minutes (e.g., 1h 30m, 1:30, 1.5).
        </div>
      )}
    </div>
  );
};

const LabourTimeInputFormikSimple = ({
  id,
  name,
  className,
  inputClassName,
  disabled,
  min = 0,
  max,
  showFormatHint = true,
  onBlur: customOnBlur,
  ...rest
}) => {
  return (
    <Field name={name || id}>
      {({ field, form }) => (
        <LabourTimeInputField
          field={field}
          form={form}
          id={id}
          className={className}
          inputClassName={inputClassName}
          disabled={disabled}
          min={min}
          max={max}
          showFormatHint={showFormatHint}
          customOnBlur={customOnBlur}
          {...rest}
        />
      )}
    </Field>
  );
};

LabourTimeInputFormikSimple.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  showFormatHint: PropTypes.bool,
  onBlur: PropTypes.func,
};

export default LabourTimeInputFormikSimple;
